import { useEffect, useRef, useState } from "react";
import {
  BookTextIcon,
  CheckIcon,
  EllipsisIcon,
  LinkIcon,
  MailIcon,
  MailPlusIcon,
  PauseIcon,
  PlusIcon,
  RepeatIcon,
  SearchIcon,
} from "lucide-react";
import { GoogleIcon, MicrosoftIcon } from "@/objects/@icons";

import { ExternalProviderEnum } from "@/__generated__/graphql";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";

import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import { TRIAGE_SUGGESTION_GROUPS } from "./TriageChangelistProposals";
import { useTriageNoDocumentStop } from "./TriageNoDocumentOptions";
import type { TriageOptionListItem } from "./TriageSuggestionsPanel";
import {
  useTriageDetailContext,
  type TriageGroupMenu,
} from "./TriageView.context";
import {
  TRIAGE_TOOLBAR_BOOK_CLASS,
  TRIAGE_TOOLBAR_BUTTON_CLASS,
  TriageDetailActions,
} from "./TriageDetailActions";
import { JOB_ICONS, TRIAGE_KEYS, type TriageJobEnum } from "./TriageView.keys";

/**
 * What is done with a payment's suggestions, on the panel's bar after its
 * search: each kind of suggestion's own actions - Match, Duplicates,
 * Transfer, Post - and the payment's Hold, Preview and Book, there while the
 * payment loads, what is on it held in place till it has.
 */
export function TriageSuggestionsToolbar({
  isLoading = false,
  isEmpty = false,
  isFolded = false,
}: {
  isLoading?: boolean;
  /** No payment picked: its controls there, with nothing to do. */
  isEmpty?: boolean;
  /**
   * The bar short of room: the kinds' menus behind one "⋯", each opening from
   * there as from its own - its key, O, opening it there too.
   */
  isFolded?: boolean;
}) {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  // Similar on, there are no kinds' menus to hold.
  const { isSimilar } = useTriageDetailContext();
  return (
    <div
      data-testid="triage-suggestions-toolbar"
      className="flex items-center gap-3 min-w-0"
    >
      <TriageLineUpTools />
      {isFolded && !isSimilar ? (
        <Popper
          isOpenState={[isMoreOpen, setIsMoreOpen]}
          placement="bottom-end"
          anchor={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Suggestions' actions"
              data-testid="triage-group-menus-more"
              className={TRIAGE_TOOLBAR_BUTTON_CLASS}
            >
              <EllipsisIcon />
            </Button>
          }
        >
          <div className="p-1">
            <TriageGroupMenus isLoading={isLoading} />
          </div>
        </Popper>
      ) : (
        <TriageGroupMenus isLoading={isLoading} />
      )}
      {/* Hold, Preview and Book, at the bar's far end: held in place, greyed,
          till there is a payment to act on. */}
      <div className="flex items-center gap-3 shrink-0">
        {isLoading || isEmpty ? (
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-3 rounded-sm"
          >
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Hold"
                disabled
                className={TRIAGE_TOOLBAR_BUTTON_CLASS}
              >
                <Icon ReactIcon={PauseIcon} size="sm" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Preview"
                disabled
                className={TRIAGE_TOOLBAR_BUTTON_CLASS}
              >
                <Icon ReactIcon={BookTextIcon} size="sm" />
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled
              className={TRIAGE_TOOLBAR_BOOK_CLASS}
            >
              <CheckIcon />
              Book
            </Button>
          </Skeleton>
        ) : (
          <TriageDetailActions />
        )}
      </div>
    </div>
  );
}

/**
 * A match picked in Similar: whether the two sides' keys move together
 * (``\\``), a chain to say so, and the mailboxes searched for the payment
 * the keys are on. Nothing counted: how many are lined up, empty or unplaced
 * is what the line-up itself shows.
 */
function TriageLineUpTools() {
  const { lineUpStatus } = useTriageDetailContext();
  if (!lineUpStatus) return null;
  const { isLocked, toggleLock, searchEmail, isSearchingEmail } = lineUpStatus;
  return (
    <div
      data-testid="triage-line-up-tools"
      className="flex items-center gap-0.5 shrink-0"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-pressed={isLocked}
            aria-label="Lock the two sides"
            data-testid="triage-line-up-lock"
            onClick={toggleLock}
            className={TRIAGE_TOOLBAR_BUTTON_CLASS}
          >
            <Icon ReactIcon={LinkIcon} size="sm" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {isLocked ? "Sides move together (\\)" : "Sides move apart (\\)"}
        </TooltipContent>
      </Tooltip>
      {/* The mailboxes searched for the payment the keys are on. */}
      {searchEmail ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              // The small spinner, as a kind's button turns one at work.
              variant="ghost"
              size="icon-sm"
              aria-label="Search email"
              data-testid="triage-line-up-search-email"
              onMouseDown={(event) => event.preventDefault()}
              onClick={searchEmail}
              className={TRIAGE_TOOLBAR_BUTTON_CLASS}
              disabled={isSearchingEmail}
            >
              {isSearchingEmail && <Spinner />}
              <Icon ReactIcon={MailIcon} size="sm" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{`Search email for this payment (${TRIAGE_KEYS.SEARCH_MAILBOXES.toUpperCase()})`}</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}

/**
 * Each kind of suggestion's button - Match, Duplicates, Transfer, Post - not
 * in Similar, whose bulk list is the panel's. Till the suggestions are drawn,
 * held in place with nothing on them.
 */
export function TriageGroupMenus({ isLoading }: { isLoading: boolean }) {
  const { groupMenus, isSimilar } = useTriageDetailContext();
  // Its key is for the keyboard: said while the keyboard is what moves about
  // the page, gone as the pointer does.
  const [isKeyboardInUse, setIsKeyboardInUse] = useState(false);
  useEffect(() => {
    const byKey = () => setIsKeyboardInUse(true);
    const byPointer = () => setIsKeyboardInUse(false);
    document.addEventListener("keydown", byKey, true);
    document.addEventListener("pointermove", byPointer, true);
    document.addEventListener("pointerdown", byPointer, true);
    return () => {
      document.removeEventListener("keydown", byKey, true);
      document.removeEventListener("pointermove", byPointer, true);
      document.removeEventListener("pointerdown", byPointer, true);
    };
  }, []);
  if (isSimilar) return null;
  return (
    <div
      data-testid="triage-group-menus"
      className="flex items-center gap-0.5 shrink-0"
    >
      {TRIAGE_SUGGESTION_GROUPS.map((group) => (
        <TriageGroupMenuButton
          key={group.id}
          menu={
            groupMenus?.find((menu) => menu.group.id === group.id) ?? {
              group,
              actions: [],
              isBusy: false,
              isFocused: false,
              isThinkingByKey: false,
            }
          }
          isLoading={isLoading}
          isKeyboardInUse={isKeyboardInUse}
        />
      ))}
    </div>
  );
}

/**
 * One kind's button, opened by its click or by O wherever the keyboard is in
 * triage - on a card of its kind, or on none for Match: its suggestions worked
 * out again (``rerunSuggestions``) and the list of all it searches opened to
 * pick from by hand; on Match first, searching each mailbox a search may look
 * in for its business, connecting one more, and copying the business's
 * forwarding address. Each does what it is for alone
 * (``useTriageNoDocumentStop``): the keyboard left where it was, and what was
 * picked kept. Its icon alone, named by its tooltip.
 */
function TriageGroupMenuButton({
  menu,
  isLoading,
  isKeyboardInUse,
}: {
  menu: TriageGroupMenu;
  isLoading: boolean;
  isKeyboardInUse: boolean;
}) {
  const { group, actions, isBusy, isFocused, isThinkingByKey } = menu;
  const pick = useTriageNoDocumentStop();
  const { rerunSuggestions, rerunningSuggestions, setReconcilingManually } =
    useTriageDetailContext();
  const [isOpen, setIsOpen] = useState(false);
  const showsKey = isFocused && isKeyboardInUse;
  // Where the keyboard was as it opened, given back as it shuts.
  const focusedRef = useRef<HTMLElement | null>(null);
  // A teammate's signed out is theirs to reconnect, and an address not set up
  // is nothing to copy: said, not done. Nor is a kind worked out again while
  // it is being.
  const isDisabled = (action: TriageOptionListItem) =>
    (action.mailbox?.isSignedOut && !action.mailbox.ownedByViewer) ||
    (action.key.startsWith("forward:") && !action.business?.address) ||
    (action.key.startsWith("rerun:") &&
      rerunningSuggestions.includes(action.group?.id ?? ""));
  return (
    <Popper
      isOpenState={[isOpen, setIsOpen]}
      isClickable={false}
      placement="bottom-start"
      floatingStyle={{ width: 300 }}
      focusRestoreRef={focusedRef}
      anchor={
        <Skeleton
          loading={isLoading}
          variant="rectangular"
          className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit] rounded-sm"
        >
          {/* Its key, O, said with its name - the kind the keyboard is on
              is the one on. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                // On with its menu open, or with the keyboard on a card of its
                // kind.
                data-on={showsKey || isOpen || undefined}
                disabled={actions.length === 0}
                data-testid="triage-group-actions"
                data-group={group.id}
                data-thinks-by-key={isThinkingByKey ? "" : undefined}
                // At work, its spinner in its icon's place - its menu still
                // open to the rest of what it offers.
                data-busy={isBusy ? "" : undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!isOpen) {
                    focusedRef.current =
                      document.activeElement as HTMLElement | null;
                  }
                  setIsOpen(!isOpen);
                }}
                aria-label={group.title}
                className={TRIAGE_TOOLBAR_BUTTON_CLASS}
              >
                {isBusy ? (
                  <Spinner />
                ) : (
                  <Icon
                    ReactIcon={JOB_ICONS[group.jobs[0] as TriageJobEnum]}
                    size="sm"
                  />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {isFocused ? `${group.title} (O)` : group.title}
            </TooltipContent>
          </Tooltip>
        </Skeleton>
      }
    >
      <div data-testid="triage-group-actions-menu">
        <List<TriageOptionListItem>
          data={actions}
          getRowKey={(action) => action.key}
          getShouldSkipRow={isDisabled}
          onItemClick={(action) => {
            if (isDisabled(action)) return;
            setIsOpen(false);
            if (action.key.startsWith("rerun:") && action.group) {
              rerunSuggestions(action.group);
              return;
            }
            if (action.key.startsWith("browse:") && action.group) {
              setReconcilingManually(action.group.tab);
              return;
            }
            pick(action, { isCardAction: true });
          }}
          autofocus
          fillHeight={false}
          size="sm"
        >
          <List.Items<TriageOptionListItem>>
            {(action: TriageOptionListItem) => (
              <List.Item
                value={action}
                data-testid={`triage-group-action-${action.key}`}
              >
                <div
                  // The menu's width: a key said at its right edge.
                  className={cn(
                    "grid grid-cols-[16px_minmax(0,1fr)] gap-x-2 items-center pl-1 pr-1 min-w-0 w-full",
                    isDisabled(action) ? "opacity-[0.5]" : "opacity-[1]",
                  )}
                >
                  <div className="flex text-muted-foreground/80">
                    <Icon
                      ReactIcon={
                        action.key.startsWith("rerun:")
                          ? RepeatIcon
                          : action.key.startsWith("browse:")
                            ? SearchIcon
                            : action.key.startsWith("forward:")
                              ? MailPlusIcon
                              : action.mailbox == null
                                ? PlusIcon
                                : action.mailbox.provider ===
                                    ExternalProviderEnum.Outlook
                                  ? MicrosoftIcon
                                  : GoogleIcon
                      }
                      size="sm"
                    />
                  </div>
                  <Typography
                    level="body-md"
                    // The whole row wide, its key at the right edge - in body
                    // text, not the list row's heavier words.
                    className="truncate flex items-center gap-1.5 w-full [&>[data-testid=hotkey-decorator]]:ml-auto"
                  >
                    {action.mailbox
                      ? `${action.mailbox.isSignedOut ? "Reconnect" : "Search"} ${action.mailbox.emailAddress}`
                      : action.title}
                    {action.key.startsWith("rerun:") && isThinkingByKey ? (
                      <HotkeyDecorator
                        hotkey={TRIAGE_KEYS.ACCEPT_META}
                        color="inherit"
                      />
                    ) : undefined}
                  </Typography>
                  {action.mailbox ? (
                    <Typography
                      level="body-xs"
                      className="truncate col-2 text-muted-foreground/80"
                    >
                      {action.mailbox.isSignedOut
                        ? action.mailbox.ownedByViewer
                          ? "Signed out by its provider"
                          : action.mailbox.ownerName
                            ? `Signed out: ${action.mailbox.ownerName} reconnects it`
                            : "Signed out: its owner reconnects it"
                        : (action.mailbox.searchFailure ??
                          (action.mailbox.ownedByViewer
                            ? "Yours"
                            : action.mailbox.ownerName
                              ? `${action.mailbox.ownerName}'s, shared with you`
                              : "Shared with you"))}
                    </Typography>
                  ) : action.key.startsWith("forward:") &&
                    action.business &&
                    !action.business.address ? (
                    <Typography
                      level="body-xs"
                      className="col-2 text-muted-foreground/80"
                    >
                      Not set up for this environment yet.
                    </Typography>
                  ) : null}
                </div>
              </List.Item>
            )}
          </List.Items>
        </List>
      </div>
    </Popper>
  );
}
